import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Grid from '@mui/system/Grid';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import useTitle from 'react-use/lib/useTitle';

import DynamicBreadcrumbs from 'src/common/components/Breadcrumbs';
import { DashboardPageHeader } from 'src/common/components/DashboardPageToolbar';
import PageBox from 'src/common/components/PageBox';
import { LanguageKeys } from 'src/common/constants/languages';
import { LanguageFlagButtons } from 'src/pages/Dashboard/System/Pages/components/LanguageFlagSwitcher';
import {
  translateEmailTemplateHelp,
  translateEmailTemplateTitle,
} from 'src/helpers/emailTemplateI18n';
import useEmailTemplatesStore, {
  EmailTemplateLocaleFields,
  EmailTemplateRecord,
} from 'src/stores/emailTemplatesStore';

const BASE_LOCALE_FIELDS: { key: keyof EmailTemplateLocaleFields; multiline?: boolean }[] = [
  { key: 'subject' },
  { key: 'greeting' },
  { key: 'intro', multiline: true },
  { key: 'actionInstructions', multiline: true },
  { key: 'buttonText' },
  { key: 'outro', multiline: true },
  { key: 'signature', multiline: true },
  { key: 'outroHtml', multiline: true },
];

function localeFieldsForTemplate(templateKey: string) {
  if (templateKey === 'participants.transfer') {
    return [
      { key: 'subject' as const },
      { key: 'greeting' as const },
      { key: 'intro' as const, multiline: true },
      { key: 'actionInstructions' as const, multiline: true },
      { key: 'buttonText' as const },
      { key: 'rejectInstructions' as const, multiline: true },
      { key: 'rejectButtonText' as const },
      { key: 'outro' as const, multiline: true },
      { key: 'signature' as const, multiline: true },
    ];
  }
  return BASE_LOCALE_FIELDS;
}

import { frostedPanelSx } from 'src/common/styles/frostedPanelStyles';

const formPaperSx = {
  ...frostedPanelSx(0.5),
  p: { xs: 2, sm: 3 },
  mb: 2,
  mt: 2,
};

export default function EmailTemplateEditor() {
  const { t } = useTranslation();
  const { encodedKey } = useParams<{ encodedKey: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const key = decodeURIComponent(encodedKey || '');

  const { current, isLoading, fetchTemplate, saveTemplate } = useEmailTemplatesStore(s => s);
  const [activeLang, setActiveLang] = useState<LanguageKeys>(LanguageKeys.en);
  const [form, setForm] = useState<EmailTemplateRecord | null>(null);

  useTitle(
    form
      ? `${translateEmailTemplateTitle(t, form.key, form.adminLabel)} - ${t('system.emails.pageTitle')} - ${t('base.defaultTitle')}`
      : `${t('system.emails.pageTitle')} - ${t('base.defaultTitle')}`,
  );

  useEffect(() => {
    if (!key) return;
    fetchTemplate(key);
  }, [key, fetchTemplate]);

  useEffect(() => {
    if (current?.key === key) {
      setForm(current);
    }
  }, [current, key]);

  const updateLocaleField = (field: keyof EmailTemplateLocaleFields, value: string) => {
    if (!form) return;
    setForm({
      ...form,
      locales: {
        ...form.locales,
        [activeLang]: {
          ...form.locales[activeLang],
          [field]: value,
        },
      },
    });
  };

  const handleSave = async () => {
    if (!form) return;
    try {
      await saveTemplate(form);
      enqueueSnackbar(t('system.emails.saveSuccess'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('system.emails.saveError'), { variant: 'error' });
    }
  };

  const localeValues = form?.locales[activeLang];
  const pageTitle = form
    ? translateEmailTemplateTitle(t, form.key, form.adminLabel)
    : t('system.emails.pageTitle');
  const templateHelp = form ? translateEmailTemplateHelp(t, form.key, form.helpText) : '';

  return (
    <>
      <DynamicBreadcrumbs />
      <DashboardPageHeader
        title={pageTitle}
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/dashboard/system/emails')}>
              {t('system.emails.backToList')}
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={!form || isLoading}
            >
              {t('system.emails.save')}
            </Button>
          </>
        }
      />
      <PageBox isLoading={isLoading && !form}>
        {form && (
          <>
            <Paper sx={formPaperSx}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 8, sm: 10, md: 10 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {templateHelp}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {t('system.emails.variablesHint')}
                  </Typography>
                </Grid>
                <Grid
                  size={{ xs: 4, sm: 2, md: 2 }}
                  sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}
                >
                  <LanguageFlagButtons value={activeLang} onChange={setActiveLang} />
                </Grid>
              </Grid>
              {localeFieldsForTemplate(form.key).map(({ key: fieldKey, multiline }) => (
                <TextField
                  key={fieldKey}
                  fullWidth
                  margin="normal"
                  label={t(`system.emails.fields.${fieldKey}`)}
                  value={localeValues?.[fieldKey] ?? ''}
                  onChange={e => updateLocaleField(fieldKey, e.target.value)}
                  multiline={multiline}
                  minRows={multiline ? 2 : undefined}
                  helperText={
                    multiline && fieldKey !== 'outroHtml'
                      ? t('system.emails.fieldHints.multiline')
                      : fieldKey === 'outroHtml'
                        ? t('system.emails.fieldHints.outroHtml')
                        : undefined
                  }
                />
              ))}
            </Paper>
          </>
        )}
      </PageBox>
    </>
  );
}
